import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Button, CloseButton, Group, Text, ThemeIcon } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen } from "lucide-react";
import { GUIDES_KEY, guidesApi, type MyGuides } from "@/lib/api/guides";
import { areaForPath, bannerGuides } from "@/lib/guides";
import { useGuidesRealtime, useMyGuides } from "@/hooks/use-guides";
import { useGuideLink } from "@/hooks/use-guide-modal";

interface AreaBannerViewProps {
  pathname: string;
  data: MyGuides | undefined;
  /** Areas dismissed in this tab, hidden before the refetch confirms it. */
  dismissedAreas?: ReadonlySet<string>;
  /** Dismiss the banner for an area (also called when the member opens the guide). */
  onDismiss?: (area: string) => void;
}

/**
 * The one-time setup nudge at the top of a portal area (#436): "There's a
 * setup guide for this page". Shown while the server flags a guide placed in
 * the area as `banner_pending`; dismissing it (or opening the guide) tells the
 * server, so it never comes back — and neither does it once the guide is
 * finished or skipped. An important new step can bring it back (server rule).
 */
export function AreaBannerView({ pathname, data, dismissedAreas, onDismiss }: AreaBannerViewProps) {
  const guideLink = useGuideLink();
  const area = data ? areaForPath(pathname, data.areas) : null;
  if (!area || dismissedAreas?.has(area)) return null;
  const guides = bannerGuides(pathname, data);
  if (guides.length === 0) return null;
  const [first] = guides;
  const others = guides.length - 1;

  return (
    <Box
      px="md"
      py="sm"
      role="region"
      aria-label="Setup guide"
      style={{
        background: "var(--mantine-primary-color-light)",
        borderBottom: "1px solid var(--mantine-color-default-border)",
      }}
    >
      <Group gap="sm" wrap="nowrap" justify="space-between" align="center">
        <Group gap="sm" wrap="nowrap" miw={0}>
          <ThemeIcon variant="filled" size={28} radius="xl" style={{ flexShrink: 0 }}>
            <BookOpen size={16} />
          </ThemeIcon>
          <Box miw={0}>
            <Text fz="sm" fw={600} lh={1.3}>
              There is a setup guide for this page: {first.title}
            </Text>
            <Text fz="xs" c="dimmed" lh={1.3}>
              {first.total > 0 ? `${first.done} of ${first.total} steps done` : ""}
              {others > 0 ? ` · ${others} more guide${others === 1 ? "" : "s"} for this page` : ""}
            </Text>
          </Box>
        </Group>
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          <Button component={Link} size="xs" {...guideLink(first.key, () => onDismiss?.(area))}>
            Open guide
          </Button>
          <CloseButton aria-label="Dismiss setup guide banner" onClick={() => onDismiss?.(area)} />
        </Group>
      </Group>
    </Box>
  );
}

/** `AreaBannerView` wired to the guides API, rendered once by `AppShell`. */
export function AreaBanner() {
  const { pathname } = useLocation();
  const qc = useQueryClient();
  // The banner is the one guide consumer mounted on every page, so it keeps
  // every guide query fresh on `guides_changed` and reconnects.
  useGuidesRealtime();
  const { data } = useMyGuides();
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const dismiss = useMutation({
    mutationFn: (area: string) => guidesApi.dismissBanner(area),
    onSettled: () => qc.invalidateQueries({ queryKey: GUIDES_KEY }),
  });

  return (
    <AreaBannerView
      pathname={pathname}
      data={data}
      dismissedAreas={dismissed}
      onDismiss={(area) => {
        setDismissed((prev) => new Set(prev).add(area));
        dismiss.mutate(area);
      }}
    />
  );
}
